<template>
  <div class="mt-3">
    <!-- Maintenance Mode Card -->
    <div class="hc-card mb-3" :class="maintenance.active ? 'border-danger' : ''">
      <div class="p-3">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h6 class="font-weight-bold mb-0">
              <i class="bi bi-cone-striped mr-1 text-dark"></i> Maintenance Mode
            </h6>
            <small v-if="maintenance.active" class="text-danger font-weight-bold">
              ACTIVE — tenants see a maintenance banner
              <span v-if="maintenance.until"> until {{ formatDate(maintenance.until) }}</span>
            </small>
            <small v-else class="text-muted">Platform is operating normally</small>
          </div>
          <div class="d-flex align-items-center" style="gap:8px;">
            <div class="custom-control custom-switch mb-0">
              <input
                type="checkbox"
                class="custom-control-input"
                id="maintenanceToggle"
                :checked="maintenance.active"
                :disabled="maintenanceSaving"
                @change="toggleMaintenance"
              />
              <label class="custom-control-label" for="maintenanceToggle"></label>
            </div>
          </div>
        </div>

        <!-- Maintenance settings (shown when activating) -->
        <div v-if="showMaintenanceForm" class="mt-3 border-top pt-3">
          <div class="form-row">
            <div class="form-group col-md-8 mb-2">
              <label class="font-weight-bold">Message to tenants</label>
              <input
                v-model="maintenanceMessage"
                class="form-control"
                placeholder="Scheduled maintenance — we'll be back shortly."
                maxlength="300"
              />
            </div>
            <div class="form-group col-md-4 mb-2">
              <label class="font-weight-bold">Auto-lift at (optional)</label>
              <input v-model="maintenanceUntil" type="datetime-local" class="form-control" />
            </div>
          </div>
          <div class="d-flex" style="gap:8px;">
            <button class="btn btn-danger" @click="enableMaintenance" :disabled="maintenanceSaving">
              <span v-if="maintenanceSaving" class="spinner-border spinner-border-sm mr-1"></span>
              Enable Maintenance Mode
            </button>
            <button class="btn btn-secondary" @click="showMaintenanceForm = false">Cancel</button>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Summary cards -->
      <div class="col-md-4 mb-3">
        <div class="hc-card text-center py-3">
          <div v-if="loading" class="spinner-border spinner-border-sm text-secondary mx-auto"></div>
          <template v-else>
            <h3 class="mb-0" :class="health.failed_jobs_count > 0 ? 'text-danger' : 'text-primary'">
              {{ health.failed_jobs_count || 0 }}
            </h3>
            <small class="text-secondary">Failed Jobs</small>
          </template>
        </div>
      </div>
      <div class="col-md-4 mb-3">
        <div class="hc-card text-center py-3">
          <div v-if="loading" class="spinner-border spinner-border-sm text-secondary mx-auto"></div>
          <template v-else>
            <h3 class="mb-0 text-dark">{{ health.pending_jobs_count || 0 }}</h3>
            <small class="text-secondary">Pending Jobs</small>
          </template>
        </div>
      </div>
      <div class="col-md-4 mb-3">
        <div class="hc-card text-center py-3">
          <div v-if="loading" class="spinner-border spinner-border-sm text-secondary mx-auto"></div>
          <template v-else>
            <h3 class="mb-0" :class="health.cache_warm ? 'text-primary' : 'text-danger'">
              {{ health.cache_warm ? 'WARM' : 'COLD' }}
            </h3>
            <small class="text-secondary">Stats Cache</small>
          </template>
        </div>
      </div>
    </div>

    <!-- Queue depths -->
    <div class="hc-card mb-3" v-if="!loading && health.queues && health.queues.length > 0">
      <div class="p-3">
        <h6 class="font-weight-bold mb-3">Queue Depths</h6>
        <div v-for="q in health.queues" :key="q.queue" class="d-flex justify-content-between py-1 border-bottom">
          <span>{{ q.queue }}</span>
          <span class="badge badge-secondary">{{ q.count }}</span>
        </div>
      </div>
    </div>

    <!-- Failed jobs list -->
    <div class="hc-card mb-3">
      <div class="p-3">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="font-weight-bold mb-0">Recent Failed Jobs</h6>
          <div class="d-flex flex-wrap" style="gap:6px;">
            <button class="btn btn-outline-secondary" @click="fetchHealth" :disabled="loading">
              <i class="bi bi-arrow-clockwise" :class="{ spin: loading }"></i> Refresh
            </button>
            <button
              v-if="health.failed_jobs_count > 0"
              class="btn btn-outline-dark"
              @click="retryAll"
              :disabled="actionLoading"
            >
              <span v-if="actionLoading === 'retry-all'" class="spinner-border spinner-border-sm mr-1"></span>
              <i v-else class="bi bi-arrow-repeat mr-1"></i>
              Retry All ({{ health.failed_jobs_count }})
            </button>
            <button class="btn btn-outline-primary" @click="refreshCache" :disabled="refreshing">
              <span v-if="refreshing" class="spinner-border spinner-border-sm mr-1"></span>
              <i v-else class="bi bi-lightning-fill mr-1"></i>
              Refresh Cache
            </button>
          </div>
        </div>

        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>
        <div v-else-if="!health.failed_jobs || health.failed_jobs.length === 0" class="text-center text-secondary py-3">
          <i class="bi bi-check-circle-fill text-primary" style="font-size:1.5rem;"></i>
          <p class="mt-2 mb-0"><small>No failed jobs</small></p>
        </div>
        <div v-else>
          <div
            v-for="job in health.failed_jobs"
            :key="job.id"
            class="border rounded p-2 mb-2"
            style="background:#fafafa;"
          >
            <div class="d-flex justify-content-between align-items-center mb-1">
              <div>
                <span class="badge badge-danger mr-1">{{ job.queue }}</span>
                <small class="text-secondary">{{ formatDate(job.failed_at) }}</small>
              </div>
              <div>
                <button
                  class="btn btn-xs btn-outline-dark mr-1"
                  style="padding:1px 7px;font-size:.75rem;"
                  @click="retryJob(job)"
                  :disabled="actionLoading === job.uuid"
                >
                  <span v-if="actionLoading === job.uuid" class="spinner-border spinner-border-sm"></span>
                  <i v-else class="bi bi-arrow-repeat"></i>
                </button>
                <button
                  class="btn btn-xs btn-outline-danger"
                  style="padding:1px 7px;font-size:.75rem;"
                  @click="deleteJob(job)"
                  :disabled="actionLoading === 'del-' + job.uuid"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
            <p class="mb-0" style="font-size:.75rem;font-family:monospace;white-space:pre-wrap;overflow:hidden;max-height:60px;">{{ job.exception }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminSystemHealth',

  data() {
    return {
      loading: false,
      refreshing: false,
      actionLoading: null,
      health: {},
      maintenance: { active: false, message: '', until: null },
      maintenanceSaving: false,
      showMaintenanceForm: false,
      maintenanceMessage: '',
      maintenanceUntil: '',
    };
  },

  mounted() {
    this.fetchHealth();
    this.fetchMaintenance();
  },

  methods: {
    async fetchHealth() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/admin/system-health');
        this.health = data;
      } finally {
        this.loading = false;
      }
    },

    async refreshCache() {
      this.refreshing = true;
      try {
        await axios.post('/api/admin/refresh-cache');
        this.$toasts.push({ message: 'Stats cache refreshed successfully', type: 'success' });
        await this.fetchHealth();
      } catch (e) {
        this.$toasts.push({ message: 'Failed to refresh cache', type: 'error' });
      } finally {
        this.refreshing = false;
      }
    },

    async retryJob(job) {
      this.actionLoading = job.uuid;
      try {
        const { data } = await axios.post(`/api/admin/failed-jobs/${job.uuid}/retry`);
        this.$toasts.push({ message: data.message, type: 'success' });
        await this.fetchHealth();
      } catch (e) {
        this.$toasts.push({ message: 'Failed to retry job', type: 'error' });
      } finally {
        this.actionLoading = null;
      }
    },

    async retryAll() {
      this.actionLoading = 'retry-all';
      try {
        const { data } = await axios.post('/api/admin/failed-jobs/retry-all');
        this.$toasts.push({ message: data.message, type: 'success' });
        await this.fetchHealth();
      } catch (e) {
        this.$toasts.push({ message: 'Failed to retry jobs', type: 'error' });
      } finally {
        this.actionLoading = null;
      }
    },

    async deleteJob(job) {
      this.actionLoading = 'del-' + job.uuid;
      try {
        await axios.delete(`/api/admin/failed-jobs/${job.uuid}`);
        this.$toasts.push({ message: 'Job removed', type: 'success' });
        await this.fetchHealth();
      } catch (e) {
        this.$toasts.push({ message: 'Failed to remove job', type: 'error' });
      } finally {
        this.actionLoading = null;
      }
    },

    formatDate(date) {
      return moment(date).format('D MMM YYYY, h:mm A');
    },

    async fetchMaintenance() {
      try {
        const { data } = await axios.get('/api/admin/maintenance-mode');
        this.maintenance = data;
      } catch (e) {
        // silent
      }
    },

    toggleMaintenance(e) {
      if (e.target.checked) {
        this.showMaintenanceForm = true;
      } else {
        this.disableMaintenance();
      }
    },

    async enableMaintenance() {
      this.maintenanceSaving = true;
      try {
        const { data } = await axios.post('/api/admin/maintenance-mode', {
          active: true,
          message: this.maintenanceMessage || undefined,
          until: this.maintenanceUntil || null,
        });
        this.maintenance = data;
        this.showMaintenanceForm = false;
        this.$toasts.push({ message: 'Maintenance mode enabled', type: 'warning' });
      } catch (e) {
        this.$toasts.push({ message: 'Failed to enable maintenance mode', type: 'error' });
      } finally {
        this.maintenanceSaving = false;
      }
    },

    async disableMaintenance() {
      this.maintenanceSaving = true;
      try {
        const { data } = await axios.post('/api/admin/maintenance-mode', { active: false });
        this.maintenance = data;
        this.$toasts.push({ message: 'Maintenance mode disabled', type: 'success' });
      } catch (e) {
        this.$toasts.push({ message: 'Failed to disable maintenance mode', type: 'error' });
      } finally {
        this.maintenanceSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.hc-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
